import "./usercss/human-support.css";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { FiArrowLeft, FiArrowUp, FiSearch, FiArchive, FiMenu, FiTrash2, FiMoreVertical } from "react-icons/fi";
import { TbMessagePlus, TbMessages } from "react-icons/tb";
import { FaHeadset } from "react-icons/fa6";
import auriImg from "../../images/auri_full-removebg-whitefont-removebg-preview.png";
import { db, auth } from "../../firebase";
import { collection, addDoc, serverTimestamp, query, where, onSnapshot, orderBy, doc, updateDoc, getDoc, getDocs, limit, deleteDoc, writeBatch, deleteField, runTransaction, setDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useLanguage } from "../../contexts/LanguageContext";
import { translations } from "../../translations";
import { validateMessage } from "../../utils/profanityFilter";
import ProfanityModal from "../../components/usercomponents/ProfanityModal";

export default function AskHuman({ onBack }) {
  const { language } = useLanguage();
  const t = translations[language];
  const [activeTab, setActiveTab] = useState("open");
  const [selectedChat, setSelectedChat] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [chats, setChats] = useState([]);
  const [peerEducatorNames, setPeerEducatorNames] = useState({});
  const [availableLanguages, setAvailableLanguages] = useState(["english", "tagalog", "bisaya"]);
  const [messages, setMessages] = useState([]);
  const [prefaceMessages, setPrefaceMessages] = useState([]);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const [isSupportTyping, setIsSupportTyping] = useState(false);
  const [started, setStarted] = useState(false);
  const [disclaimerConfirmed, setDisclaimerConfirmed] = useState(false);
  const [showLanguageOptions, setShowLanguageOptions] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [showTopicOptions, setShowTopicOptions] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [userInQueue, setUserInQueue] = useState(false);
  const [isWaitingForEducator, setIsWaitingForEducator] = useState(false);
  const [currentInquiryId, setCurrentInquiryId] = useState(null);
  const [activeChatId, setActiveChatId] = useState(null);
  const [chatMode, setChatMode] = useState("waiting"); // "waiting" | "chat"
  const [nextMessageOrder, setNextMessageOrder] = useState(0);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const [currentInquiryStatus, setCurrentInquiryStatus] = useState(null);
  const [currentTicketNumber, setCurrentTicketNumber] = useState(null);
  const [hasAddedInitialWaitingMessage, setHasAddedInitialWaitingMessage] = useState(false);
  const [hasAddedAssignedMessage, setHasAddedAssignedMessage] = useState(false);
  const [isWaitingInQueue, setIsWaitingInQueue] = useState(false);
  const [originalPreferredLanguage, setOriginalPreferredLanguage] = useState(null);
  const [inquirySubmittedAt, setInquirySubmittedAt] = useState(null);
  const [isChatEscalated, setIsChatEscalated] = useState(false);
  const [escalationStatus, setEscalationStatus] = useState(null);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [chatToArchive, setChatToArchive] = useState(null);
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [chatToOpen, setChatToOpen] = useState(null);
  const [showProfanityModal, setShowProfanityModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [chatToDelete, setChatToDelete] = useState(null);
  const [chatsLoading, setChatsLoading] = useState(true);
  const [chatsLoaded, setChatsLoaded] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesLoaded, setMessagesLoaded] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isOutsideOfficeHours, setIsOutsideOfficeHours] = useState(false);

  const typingTimeoutRef = useRef(null);
  const messagesScrollRef = useRef(null);
  const textareaRef = useRef(null);
  const messageOrderRef = useRef(1);
  const peerEducatorsCacheRef = useRef([]);
  const peerEducatorsNamesRef = useRef({});
  const persistedOnboardingMessages = useRef(new Set());
  
  // Timer for periodic peer educator cache refresh (instead of continuous listener)
  const peerEducatorsRefreshTimerRef = useRef(null);
  const CACHE_REFRESH_INTERVAL = 60000; // 60 seconds
  
  // Track chat status from listener to eliminate getDoc pre-write read
  const currentChatStatusRef = useRef(null);
  const currentChatEscalationStatusRef = useRef(null);
  
  // Ref for prefaceMessages to avoid dependency issues in message listener
  const prefaceMessagesRef = useRef([]);

  const allocateMessageOrder = () => {
    const order = messageOrderRef.current;
    messageOrderRef.current += 1;
    setNextMessageOrder(messageOrderRef.current);
    return order;
  };

  const syncMessageOrderFromMessages = (msgs) => {
    let maxOrder = -1;
    msgs.forEach((msg) => {
      if (msg.order !== undefined && msg.order > maxOrder) {
        maxOrder = msg.order;
      }
    });
    const next = maxOrder + 1;
    messageOrderRef.current = next;
    setNextMessageOrder(next);
  };

  const reserveChatMessageOrder = async (chatId) => {
    return allocateMessageOrder();
  };

  // Monitor authentication state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthenticated(!!user);
      if (!user) {
        console.error("User not authenticated");
      }
    });
    return () => unsubscribe();
  }, [auth]);

  useEffect(() => {
    const checkOfficeHours = () => {
      const now = new Date();
      const hourFormatter = new Intl.DateTimeFormat("en-US", {
        timeZone: "Asia/Manila",
        hour: "numeric",
        hour12: false
      });
      const hour = parseInt(hourFormatter.format(now));
      const outsideHours = hour < 8 || hour >= 17;
      setIsOutsideOfficeHours(outsideHours);

      // Set initial message
      if (!outsideHours) {
        const initialMessage = { sender: "auri", text: "Hi there! You're about to chat with an AuraVit Peer Educator.", order: 0 };
        setMessages([initialMessage]);
        setPrefaceMessages([initialMessage]);
      } else {
        // Show office hours message when outside hours
        const officeHoursMessage = {
          sender: "system",
          text: "Human support is currently unavailable",
          subtext: "Office Hours: 8:00 AM - 5:00 PM (Asia/Manila Time)\nPlease try again during office hours to connect with a Peer Educator.",
          order: 0,
          isOfficeHours: true
        };
        setMessages([officeHoursMessage]);
        setPrefaceMessages([officeHoursMessage]);
      }
    };

    checkOfficeHours();
    const interval = setInterval(checkOfficeHours, 60000); // Check every minute

    return () => clearInterval(interval);
  }, []);
  

  // Periodic peer educator cache refresh (instead of continuous listener to avoid global fan-out)
  useEffect(() => {
    const refreshPeerEducatorsCache = async () => {
      try {
        const usersRef = collection(db, "users");
        const peerEducatorsQuery = query(
          usersRef,
          where("roles", "array-contains", "Peer Educator")
        );
        const snapshot = await getDocs(peerEducatorsQuery);
        const docs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        peerEducatorsCacheRef.current = docs;

        const languagesSet = new Set(["english", "tagalog", "bisaya"]);
        const namesBuf = { ...peerEducatorsNamesRef.current };
        for (const docSnap of docs) {
          if (docSnap.languageSpoken && Array.isArray(docSnap.languageSpoken)) {
            docSnap.languageSpoken.forEach(lang => languagesSet.add(lang.toLowerCase()));
          }
          const fn = docSnap.fullName || "";
          const first = fn.split(' ')[0] || "Peer Educator";
          namesBuf[docSnap.id] = first;
        }
        peerEducatorsNamesRef.current = namesBuf;
        setPeerEducatorNames(prev => {
          let changed = false;
          const merged = { ...prev };
          for (const k of Object.keys(namesBuf)) {
            if (merged[k] !== namesBuf[k]) {
              merged[k] = namesBuf[k];
              changed = true;
            }
          }
          return changed ? merged : prev;
        });
        setAvailableLanguages(Array.from(languagesSet));
      } catch (error) {
        console.error("Error refreshing peer educators cache:", error);
      }
    };

    // Initial load
    refreshPeerEducatorsCache();

    // Set up periodic refresh every 60 seconds instead of continuous listener
    // This eliminates the global fan-out issue where every peer educator change triggers reads for all users
    peerEducatorsRefreshTimerRef.current = setInterval(() => {
      refreshPeerEducatorsCache();
    }, CACHE_REFRESH_INTERVAL);

    return () => {
      if (peerEducatorsRefreshTimerRef.current) {
        clearInterval(peerEducatorsRefreshTimerRef.current);
        peerEducatorsRefreshTimerRef.current = null;
      }
    };
  }, []);

  // Helper function to format timestamps (handles both Date objects and Firebase timestamps)
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    
    // If it's a Firebase timestamp (has toDate method)
    if (typeof timestamp.toDate === 'function') {
      return timestamp.toDate().toLocaleTimeString();
    }
    
    // If it's already a Date object
    if (timestamp instanceof Date) {
      return timestamp.toLocaleTimeString();
    }
    
    // If it's a string or number, try to convert to Date
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString();
    } catch (error) {
      console.error("Error formatting timestamp:", error);
      return "";
    }
  };

  // Generate unique ticket number
  const generateTicketNumber = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    
    return `TKT-${year}${month}${day}-${hours}${minutes}${seconds}-${random}`;
  };

  // Helper function to get the actual user inquiry (skips "I Understand")
  const getUserInquiryMessage = () => {
    const userMessages = messages
      .filter(m => m.sender === "user" && m.text !== t.iUnderstand);
    if (userMessages.length > 0) {
      return userMessages[userMessages.length - 1].text;
    }
    return "";
  };

  // Helper function to get status badge for chat
  const getStatusBadge = (chat) => {
    if (chat.status === 'waiting') {
      return { text: 'Waiting', color: '#f59e0b', bgColor: '#fef3c7' };
    } else if (chat.status === 'active') {
      return { text: 'Active', color: '#10b981', bgColor: '#d1fae5' };
    } else if (chat.status === 'inactive') {
      return { text: 'Inactive', color: '#f59e0b', bgColor: '#fef3c7' };
    } else if (chat.status === 'resolved') {
      return { text: 'Resolved', color: '#3b82f6', bgColor: '#dbeafe' };
    } else if (chat.status === 'escalated') {
      if (chat.escalationStatus === 'resolved') {
        return { text: 'Escalated - Resolved', color: '#6366f1', bgColor: '#e0e7ff' };
      } else {
        return { text: 'Escalated', color: '#ef4444', bgColor: '#fee2e2' };
      }
    }
    return null;
  };

  // Check educator availability to determine if language fallback is needed
  // Store a message in the waiting inquiry's messages subcollection
  const storeInquiryMessage = async (inquiryId, { senderRole, text, order, senderId }) => {
    if (!inquiryId) return;
    
    const userId = auth.currentUser?.uid;
    if (senderRole === "user" && !userId) {
      console.error("User not authenticated - cannot store message");
      return;
    }
    
    try {
      await addDoc(collection(db, "inquiries", inquiryId, "messages"), {
        senderId: senderId || (senderRole === "user" ? userId : "system"),
        senderRole,
        text,
        order,
        createdAt: serverTimestamp()
      });
    } catch (error) {
      console.error("Error storing inquiry message:", error);
    }
  };

  // Consolidated inquiry update function to avoid duplicate writes
  const updateInquiryMetadata = async (inquiryId, metadata) => {
    if (!inquiryId) return;
    try {
      await updateDoc(doc(db, "inquiries", inquiryId), {
        ...metadata,
        lastActivityAt: serverTimestamp()
      });
    } catch (error) {
      console.error("Error updating inquiry metadata:", error);
    }
  };

  // Backfill UI messages into inquiry subcollection after inquiry is created (optimized with parallel writes)
  const backfillInquiryMessages = async (inquiryId, msgs) => {
    const filtered = msgs;    if (filtered.length === 0) return;
    
    const userId = auth.currentUser?.uid;
    if (!userId) {
      console.error("User not authenticated - cannot backfill messages");
      return;
    }
    
    try {
      // Use Promise.all for parallel writes instead of sequential await
      const writePromises = filtered.map((msg, i) => {
        const senderRole = msg.sender === "user" ? "user" : "system";
        return addDoc(collection(db, "inquiries", inquiryId, "messages"), {
          senderId: senderRole === "user" ? userId : "system",
          senderRole,
          text: msg.text,
          order: i,
          createdAt: serverTimestamp()
        });
      });
      
      await Promise.all(writePromises);
      
      messageOrderRef.current = filtered.length;
      setNextMessageOrder(filtered.length);
    } catch (error) {
      console.error("Error backfilling inquiry messages:", error);
      // Fallback to sequential writes if parallel fails
      for (let i = 0; i < filtered.length; i++) {
        const msg = filtered[i];
        const senderRole = msg.sender === "user" ? "user" : "system";
        await storeInquiryMessage(inquiryId, {
          senderRole,
          text: msg.text,
          order: i,
          senderId: senderRole === "user" ? userId : "system"
        });
      }
      messageOrderRef.current = filtered.length;
      setNextMessageOrder(filtered.length);
    }
  };

  // Persist onboarding message to chat or waiting inquiry subcollection (optimized with client-side tracking)
  const persistOnboardingMessage = async (text, senderRole, explicitOrder = null) => {
    const messageOrder = explicitOrder !== null ? explicitOrder : allocateMessageOrder();
    
    // Create unique key for this message (include conversation context to prevent collisions)
    const conversationContext = activeChatId || currentInquiryId || 'new';
    const messageKey = `${conversationContext}:${senderRole}:${text}`;

    const userId = auth.currentUser?.uid;
    if (senderRole === "user" && !userId) {
      console.error("User not authenticated - cannot persist message");
      return;
    }

    if (activeChatId && !onboardingCompleted) {
      try {
        // Check client-side cache first to avoid Firestore query
        if (persistedOnboardingMessages.current.has(messageKey)) {
          return;
        }
        
        const messagesRef = collection(db, "chats", activeChatId, "messages");
        await addDoc(messagesRef, {
          senderId: senderRole === "user" ? userId : "system",
          senderRole,
          text,
          createdAt: serverTimestamp(),
          order: messageOrder
        });
        
        // Mark as persisted in client-side cache
        persistedOnboardingMessages.current.add(messageKey);
      } catch (error) {
        console.error("Error storing onboarding message to chat:", error);
      }
    } else if (currentInquiryId && !activeChatId) {
      try {
        // Check client-side cache first to avoid Firestore query
        if (persistedOnboardingMessages.current.has(messageKey)) {
          return;
        }
        
        await storeInquiryMessage(currentInquiryId, {
          senderRole,
          text,
          order: messageOrder
        });
        
        // Mark as persisted in client-side cache
        persistedOnboardingMessages.current.add(messageKey);
      } catch (error) {
        console.error("Error storing onboarding message to inquiry:", error);
      }
    }
  };

  // Batch persist multiple onboarding messages (optimizes multiple writes into single batch)
  const persistOnboardingMessagesBatch = async (messages) => {
    if (!messages || messages.length === 0) return;
    
    const conversationContext = activeChatId || currentInquiryId || 'new';
    const userId = auth.currentUser?.uid;
    
    if (activeChatId && !onboardingCompleted) {
      try {
        const batch = writeBatch(db);
        const messagesRef = collection(db, "chats", activeChatId, "messages");
        
        for (const { text, senderRole, explicitOrder } of messages) {
          const messageOrder = explicitOrder !== null ? explicitOrder : allocateMessageOrder();
          const messageKey = `${conversationContext}:${senderRole}:${text}`;
          
          // Skip if already persisted
          if (persistedOnboardingMessages.current.has(messageKey)) {
            continue;
          }
          
          // Skip user messages if not authenticated
          if (senderRole === "user" && !userId) {
            console.error("User not authenticated - cannot persist message");
            continue;
          }
          
          const newDocRef = doc(messagesRef);
          batch.set(newDocRef, {
            senderId: senderRole === "user" ? userId : "system",
            senderRole,
            text,
            createdAt: serverTimestamp(),
            order: messageOrder
          });
          
          persistedOnboardingMessages.current.add(messageKey);
        }
        
        await batch.commit();
      } catch (error) {
        console.error("Error batch storing onboarding messages to chat:", error);
      }
    } else if (currentInquiryId && !activeChatId) {
      try {
        // For inquiries, we can't use batch across different collections, so fall back to individual writes
        for (const { text, senderRole, explicitOrder } of messages) {
          const messageOrder = explicitOrder !== null ? explicitOrder : allocateMessageOrder();
          const messageKey = `${conversationContext}:${senderRole}:${text}`;
          
          if (persistedOnboardingMessages.current.has(messageKey)) {
            continue;
          }
          
          await storeInquiryMessage(currentInquiryId, {
            senderRole,
            text,
            order: messageOrder
          });
          
          persistedOnboardingMessages.current.add(messageKey);
        }
      } catch (error) {
        console.error("Error batch storing onboarding messages to inquiry:", error);
      }
    }
  };

  const buildWaitingSubmissionMessage = (ticketNumber) => {
    let message = `${t.yourInquirySubmittedTicket} ${ticketNumber}\n\n${t.waitingForPeerEducator}`;
    return message;
  };

  const addWaitingSubmissionMessage = async (inquiryId, ticketNumber) => {
    const auriMessage = buildWaitingSubmissionMessage(ticketNumber);
    const order = allocateMessageOrder();

    setMessages((prev) => {
      if (prev.some((msg) => msg.text?.includes(ticketNumber))) {
        return prev;
      }
      return [
        ...prev,
        {
          sender: "auri",
          text: auriMessage,
          createdAt: new Date(),
          order
        }
      ];
    });
    setHasAddedInitialWaitingMessage(true);

    if (inquiryId) {
      await storeInquiryMessage(inquiryId, {
        senderRole: "system",
        text: auriMessage,
        order,
        senderId: "system"
      });
    }
  };

  const markInquiryMessagesReady = async (inquiryId) => {
    await updateDoc(doc(db, "inquiries", inquiryId), { messagesReady: true });
    setCurrentInquiryStatus("waiting");
    setInquirySubmittedAt(Date.now());
  };

  // Create inquiry in Firestore
  const createInquiry = async (messagePreview, msgsOverride = null) => {
    try {
      // Get current user ID (you may need to adjust this based on your auth implementation)
      const userId = auth.currentUser?.uid;
      if (!userId) {
        console.error("User not authenticated - cannot create inquiry");
        throw new Error("Authentication required");
      }
      
      const ticketNumber = generateTicketNumber();
      
      // Reset state for new inquiry
      setHasAddedInitialWaitingMessage(false);
      setHasAddedAssignedMessage(false);
      setOriginalPreferredLanguage(selectedLanguage);
      setIsWaitingInQueue(true);
      
      const inquiryData = {
        userId,
        messagePreview,
        status: "waiting",
        messagesReady: false,
        ticketNumber,
        createdAt: serverTimestamp(),
        lastActivityAt: serverTimestamp(),
        userLastActivityAt: serverTimestamp(),
        lastMessage: messagePreview,
        preferredLanguage: selectedLanguage,
        inquiryTopic: selectedTopic
      };

      const docRef = await addDoc(collection(db, "inquiries"), inquiryData);
      setCurrentInquiryId(docRef.id);
      setCurrentTicketNumber(ticketNumber);
      setCurrentInquiryStatus("waiting");

      // Persist onboarding messages before the ticket message is shown or assignment runs
      await backfillInquiryMessages(docRef.id, msgsOverride || messages);
      await addWaitingSubmissionMessage(docRef.id, ticketNumber);
      await markInquiryMessagesReady(docRef.id);

      return { inquiryId: docRef.id, ticketNumber };
    } catch (error) {
      console.error("Error creating inquiry:", error);
      throw error;
    }
  };

  // Handle I Understand button
  const handleIUnderstand = async () => {
    setDisclaimerConfirmed(true);

    // Collect all onboarding messages to batch them
    const onboardingMessages = [];
    
    // Add to UI only if no chat exists (otherwise Firestore listener will handle it)
    if (!activeChatId) {
      const order = allocateMessageOrder();
      setMessages((prev) => [...prev, { sender: "user", text: t.iUnderstand, order }]);
      if (currentInquiryId) {
        onboardingMessages.push({ text: t.iUnderstand, senderRole: "user", explicitOrder: order });
      }
    } else if (activeChatId && !onboardingCompleted) {
      onboardingMessages.push({ text: t.iUnderstand, senderRole: "user" });
    } else if (currentInquiryId && !activeChatId) {
      onboardingMessages.push({ text: t.iUnderstand, senderRole: "user" });
    }

    setTimeout(async () => {
      setIsSupportTyping(true);

      setTimeout(async () => {
        setIsSupportTyping(false);
        if (!activeChatId) {
          const order = allocateMessageOrder();
          setMessages((prev) => [...prev, { sender: "auri", text: t.greatChoosePreferredLanguage, order }]);
          if (currentInquiryId) {
            onboardingMessages.push({ text: t.greatChoosePreferredLanguage, senderRole: "system", explicitOrder: order });
          }
        } else if (activeChatId && !onboardingCompleted) {
          onboardingMessages.push({ text: t.greatChoosePreferredLanguage, senderRole: "system" });
        } else if (currentInquiryId && !activeChatId) {
          onboardingMessages.push({ text: t.greatChoosePreferredLanguage, senderRole: "system" });
        }
        
        // Batch all onboarding messages at once
        if (onboardingMessages.length > 0) {
          await persistOnboardingMessagesBatch(onboardingMessages);
        }
        
        setShowLanguageOptions(true);
      }, 2000);
    }, 850);
  };

  // Handle language selection
  const handleLanguageSelect = async (lang) => {
    setSelectedLanguage(lang);
    setShowLanguageOptions(false);
    let langLabel;
    if (lang === "english") langLabel = t.english;
    else if (lang === "tagalog") langLabel = t.tagalog;
    else if (lang === "bisaya") langLabel = t.bisaya;
    else langLabel = lang.charAt(0).toUpperCase() + lang.slice(1);
    
    // Collect onboarding messages for batching
    const onboardingMessages = [];
    
    // Add to UI only if no chat exists
    if (!activeChatId) {
      const order = allocateMessageOrder();
      setMessages((prev) => [...prev, { sender: "user", text: langLabel, order }]);
      if (currentInquiryId) {
        onboardingMessages.push({ text: langLabel, senderRole: "user", explicitOrder: order });
      }
    } else {
      onboardingMessages.push({ text: langLabel, senderRole: "user" });
    }

    if (currentInquiryId) {
      // Existing inquiry exists - prepare to let user re-send inquiry
      try {
        await updateInquiryMetadata(currentInquiryId, {
          preferredLanguage: lang,
          status: "waiting"
        });
        setOriginalPreferredLanguage(lang);
        setIsWaitingInQueue(false);
        setInquirySubmittedAt(null);
        setUserInQueue(false);
        setIsWaitingForEducator(false);
        setHasAddedInitialWaitingMessage(false);
        
        // Show topic selection message
        setTimeout(async () => {
          setIsSupportTyping(true);

          setTimeout(async () => {
            setIsSupportTyping(false);
            // Add to UI only if no chat exists
            if (!activeChatId) {
              const order = allocateMessageOrder();
              setMessages((prev) => [...prev, { sender: "auri", text: t.excellentChooseTopic, order }]);
              if (currentInquiryId) {
                onboardingMessages.push({ text: t.excellentChooseTopic, senderRole: "system", explicitOrder: order });
              }
            } else {
              onboardingMessages.push({ text: t.excellentChooseTopic, senderRole: "system" });
            }
            
            // Batch all onboarding messages
            if (onboardingMessages.length > 0) {
              await persistOnboardingMessagesBatch(onboardingMessages);
            }
            
            setShowTopicOptions(true);
          }, 2000);
        }, 850);
      } catch (error) {
        console.error("Error updating existing inquiry:", error);
      }
    } else {
      // No existing inquiry - proceed with original flow
      setIsWaitingInQueue(false);
      setOriginalPreferredLanguage(null);
      setInquirySubmittedAt(null);

      setTimeout(async () => {
        setIsSupportTyping(true);

        setTimeout(async () => {
          setIsSupportTyping(false);
          if (!activeChatId) {
            const order = allocateMessageOrder();
            setMessages((prev) => [...prev, { sender: "auri", text: t.excellentChooseTopic, order }]);
            onboardingMessages.push({ text: t.excellentChooseTopic, senderRole: "system", explicitOrder: order });
          } else {
            onboardingMessages.push({ text: t.excellentChooseTopic, senderRole: "system" });
          }
          
          // Batch all onboarding messages
          if (onboardingMessages.length > 0) {
            await persistOnboardingMessagesBatch(onboardingMessages);
          }
          
          setShowTopicOptions(true);
        }, 2000);
      }, 850);
    }
  };

  // Handle topic selection
  const handleTopicSelect = async (topic) => {
    setSelectedTopic(topic);
    setShowTopicOptions(false);
    
    // Collect onboarding messages for batching
    const onboardingMessages = [];
    
    // Add to UI only if no chat exists
    if (!activeChatId) {
      const order = allocateMessageOrder();
      setMessages((prev) => [...prev, { sender: "user", text: topic, order }]);
      if (currentInquiryId) {
        onboardingMessages.push({ text: topic, senderRole: "user", explicitOrder: order });
      }
    } else {
      onboardingMessages.push({ text: topic, senderRole: "user" });
    }

    // Update inquiry with selected topic if inquiry exists
    if (currentInquiryId) {
      try {
        await updateInquiryMetadata(currentInquiryId, {
          inquiryTopic: topic
        });
      } catch (error) {
        console.error("Error updating inquiry topic:", error);
      }
    }

    // Show thank you message
    setTimeout(async () => {
      setIsSupportTyping(true);

      setTimeout(async () => {
        setIsSupportTyping(false);
        setIsWaitingForEducator(false); // Enable input after thank you message
        setUserInQueue(false); // Reset userInQueue to allow normal message sending
        setStarted(true); // Enable input field
        
        // Add to UI only if no chat exists
        if (!activeChatId) {
          const order = allocateMessageOrder();
          setMessages((prev) => [...prev, { sender: "auri", text: t.thankYouForSharing, order }]);
          onboardingMessages.push({ text: t.thankYouForSharing, senderRole: "system", explicitOrder: order });
        } else {
          onboardingMessages.push({ text: t.thankYouForSharing, senderRole: "system" });
        }
        
        // Batch all onboarding messages
        if (onboardingMessages.length > 0) {
          await persistOnboardingMessagesBatch(onboardingMessages);
        }
      }, 2000);
    }, 850);
  };

  const fetchPeerEducatorName = async (peerEducatorId) => {
    if (!peerEducatorId) return "Peer Educator";

    if (peerEducatorNames[peerEducatorId]) {
      return peerEducatorNames[peerEducatorId];
    }

    const cached = peerEducatorsCacheRef.current.find(d => d.id === peerEducatorId);
    if (cached && cached.fullName) {
      const firstName = String(cached.fullName).split(' ')[0] || "Peer Educator";
      peerEducatorsNamesRef.current[peerEducatorId] = firstName;
      setPeerEducatorNames(prev => prev[peerEducatorId] === firstName ? prev : { ...prev, [peerEducatorId]: firstName });
      return firstName;
    }

    try {
      const userDoc = await getDoc(doc(db, 'users', peerEducatorId));
      if (userDoc.exists()) {
        const data = userDoc.data();
        const fullName = data.fullName || "";
        const firstName = fullName.split(' ')[0] || "Peer Educator";
        peerEducatorsNamesRef.current[peerEducatorId] = firstName;
        setPeerEducatorNames(prev => ({
          ...prev,
          [peerEducatorId]: firstName
        }));
        return firstName;
      }
    } catch (error) {
      console.error('Error fetching peer educator name:', error);
    }
    return "Peer Educator";
  };

  useEffect(() => {
    const fetchAllPeerEducatorNames = async () => {
      const uniquePeerEducatorIds = [...new Set(chats
        .filter(chat => chat.peerEducatorId)
        .map(chat => chat.peerEducatorId)
      )];

      const uncachedIds = uniquePeerEducatorIds.filter(id => !peerEducatorNames[id]);

      if (uncachedIds.length === 0) return;

      const cacheHitNames = {};
      const trulyUncached = [];
      const educators = peerEducatorsCacheRef.current || [];
      for (const id of uncachedIds) {
        const hit = educators.find(d => d.id === id);
        if (hit && hit.fullName) {
          cacheHitNames[id] = String(hit.fullName).split(' ')[0] || "Peer Educator";
        } else {
          trulyUncached.push(id);
        }
      }

      try {
        let results = [];
        if (trulyUncached.length > 0) {
          const namePromises = trulyUncached.map(async (id) => {
            const userDoc = await getDoc(doc(db, 'users', id));
            if (userDoc.exists()) {
              const data = userDoc.data();
              const fullName = data.fullName || "";
              const firstName = fullName.split(' ')[0] || "Peer Educator";
              return { id, firstName };
            }
            return { id, firstName: "Peer Educator" };
          });
          results = await Promise.all(namePromises);
        }

        for (const k of Object.keys(cacheHitNames)) {
          results.push({ id: k, firstName: cacheHitNames[k] });
        }

        if (results.length === 0) return;

        setPeerEducatorNames(prev => {
          const newNames = { ...prev };
          results.forEach(({ id, firstName }) => {
            newNames[id] = firstName;
            peerEducatorsNamesRef.current[id] = firstName;
          });
          return newNames;
        });
      } catch (error) {
        console.error('Error batch fetching peer educator names:', error);
      }
    };

    if (chats.length > 0) {
      fetchAllPeerEducatorNames();
    }
  }, [chats]);

  // Fetch user's chats and inquiries from Firestore with realtime listeners
  useEffect(() => {
    const userId = auth.currentUser?.uid;
    if (!userId) return;

    setChatsLoading(true);
    setChatsLoaded(false);

    // Process chat documents (optimized to avoid N+1 query pattern)
    const processChatDoc = (document) => {
      const chatData = document.data();
      const inquiryTopic = chatData.inquiryTopic || null;
      // Use existing chat metadata instead of querying messages subcollection
      const firstMessage = chatData.messagePreview || chatData.lastMessage || "No messages yet";
      
      return {
        id: document.id,
        title: inquiryTopic || (chatData.ticketNumber ? `Ticket ${chatData.ticketNumber}` : "Support Chat"),
        time: formatInquiryTime(chatData.lastActivityAt || chatData.createdAt),
        archived: chatData.status === "resolved",
        status: chatData.status || "active",
        ticketNumber: chatData.ticketNumber,
        inquiryTopic: inquiryTopic,
        messagePreview: firstMessage,
        chatId: document.id,
        inquiryId: chatData.inquiryId || null,
        source: 'chat',
        lastActivityAt: chatData.lastActivityAt,
        createdAt: chatData.createdAt,
        peerEducatorId: chatData.assignedTo || chatData.peerEducatorId || null,
        preferredLanguage: chatData.preferredLanguage || null,
        escalationStatus: chatData.escalationStatus || null
      };
    };

    // Process inquiry documents
    const processInquiryDoc = (document) => {
      const inquiryData = document.data();
      
      return {
        id: document.id,
        title: inquiryData.inquiryTopic || (inquiryData.ticketNumber ? `Ticket ${inquiryData.ticketNumber}` : "Support Chat"),
        time: formatInquiryTime(inquiryData.lastActivityAt || inquiryData.createdAt),
        archived: inquiryData.status === "resolved",
        status: inquiryData.status || "waiting",
        ticketNumber: inquiryData.ticketNumber,
        inquiryTopic: inquiryData.inquiryTopic,
        messagePreview: inquiryData.messagePreview || "No messages yet",
        chatId: null,
        inquiryId: document.id,
        source: 'inquiry',
        lastActivityAt: inquiryData.lastActivityAt,
        createdAt: inquiryData.createdAt,
        peerEducatorId: null,
        preferredLanguage: inquiryData.preferredLanguage || null
      };
    };

    let currentChatsData = [];
    let currentInquiriesData = [];
    let mounted = true;

    // Listen for changes to chats - OPTIMIZED
    const chatsQuery = query(
      collection(db, "chats"),
      where("userId", "==", userId),
      orderBy("createdAt", "desc"),
      limit(20) // Add limit
    );

    const unsubscribeChats = onSnapshot(chatsQuery, (snapshot) => {
      if (!mounted) return;
      currentChatsData = snapshot.docs.map(doc => processChatDoc(doc));
      updateCombinedChats(currentChatsData, currentInquiriesData);
      setChatsLoading(false);
      setChatsLoaded(true);
    }, (error) => {
      console.error("Error listening for chats:", error);
      setChatsLoading(false);
      setChatsLoaded(true);
    });

    // Listen for waiting inquiries only - OPTIMIZED
    const inquiriesQuery = query(
      collection(db, "inquiries"),
      where("userId", "==", userId),
      where("status", "==", "waiting"),
      orderBy("lastActivityAt", "desc"),
      limit(10) // Add limit
    );

    const unsubscribeInquiries = onSnapshot(inquiriesQuery, (snapshot) => {
      if (!mounted) return;
      currentInquiriesData = snapshot.docs.map(processInquiryDoc);
      updateCombinedChats(currentChatsData, currentInquiriesData);
      setChatsLoading(false);
      setChatsLoaded(true);
    }, (error) => {
      console.error("Error listening for inquiries:", error);
      setChatsLoading(false);
      setChatsLoaded(true);
    });

    // Cleanup listeners on unmount
    return () => {
      mounted = false;
      unsubscribeChats();
      unsubscribeInquiries();
    };
  }, []);

  // Update message when inquiry status changes to 'assigned'
  useEffect(() => {
    if (currentInquiryStatus === "assigned" && 
        !hasAddedAssignedMessage && 
        hasAddedInitialWaitingMessage &&
        !isChatEscalated) {
      
      // Reset waiting queue state
      setIsWaitingInQueue(false);
      
      // Add a small delay before showing the assigned message
      const delay = setTimeout(() => {
        const currentOrder = allocateMessageOrder();
        const assignedMessage = {
          sender: "auri",
          text: "Your inquiry has been assigned to a Peer Educator. Waiting for them to enter the chat...",
          createdAt: new Date(),
          order: currentOrder
        };
        
        setMessages((prev) => [...prev, assignedMessage]);
        setHasAddedAssignedMessage(true);
      }, 800); // 800ms delay
      
      return () => clearTimeout(delay);
    }
  }, [currentInquiryStatus, hasAddedInitialWaitingMessage, hasAddedAssignedMessage, isChatEscalated]);

  // Function to combine and deduplicate chats and inquiries
  const updateCombinedChats = (chats, inquiries) => {
    // Create a map to deduplicate by ticket number
    const ticketMap = new Map();
    
    // Add all chats first (they take precedence)
    chats.forEach(chat => {
      if (chat.ticketNumber) {
        ticketMap.set(chat.ticketNumber, chat);
      } else {
        // For chats without ticket numbers, use the chat ID as key
        ticketMap.set(`chat_${chat.id}`, chat);
      }
    });
    
    // Add inquiries only if their ticket number doesn't exist in the map
    // OR if the inquiry has a more recent lastActivityAt
    inquiries.forEach(inquiry => {
      const existing = inquiry.ticketNumber ? ticketMap.get(inquiry.ticketNumber) : ticketMap.get(`inquiry_${inquiry.id}`);
      
      if (!existing) {
        if (inquiry.ticketNumber) {
          ticketMap.set(inquiry.ticketNumber, inquiry);
        } else {
          ticketMap.set(`inquiry_${inquiry.id}`, inquiry);
        }
      } else {
        // If already exists, update with most recent activity info
        const getTime = (item) => {
          if (item.lastActivityAt) return item.lastActivityAt.toDate?.()?.getTime() || item.lastActivityAt.seconds * 1000 || 0;
          return item.createdAt?.toDate?.()?.getTime() || item.createdAt?.seconds * 1000 || 0;
        };
        
        if (getTime(inquiry) > getTime(existing)) {
          // Merge inquiry activity into existing chat entry
          ticketMap.set(inquiry.ticketNumber || `inquiry_${inquiry.id}`, {
            ...existing,
            lastActivityAt: inquiry.lastActivityAt,
            messagePreview: inquiry.messagePreview || existing.messagePreview
          });
        }
      }
    });
    
    // Convert back to array and sort by latest activity
    const combined = Array.from(ticketMap.values());
    const sorted = combined.sort((a, b) => {
      const getTime = (chat) => {
        if (chat.lastActivityAt) return chat.lastActivityAt.toDate?.()?.getTime() || chat.lastActivityAt.seconds * 1000 || 0;
        return chat.createdAt?.toDate?.()?.getTime() || chat.createdAt?.seconds * 1000 || 0;
      };
      return getTime(b) - getTime(a);
    });
      
    setChats(sorted);
  };

  // Listen for chat session when inquiry is assigned (transferred to chats collection) - OPTIMIZED
  useEffect(() => {
    const userId = auth.currentUser?.uid;
    if (!userId || !currentInquiryId) return;

    let mounted = true;

    const q = query(
      collection(db, "chats"),
      where("userId", "==", userId),
      where("inquiryId", "==", currentInquiryId),
      limit(1)
    );

    const unsubscribe = onSnapshot(q, 
      (querySnapshot) => {
        if (!mounted) return;
        
        if (!querySnapshot.empty && querySnapshot.docs.length > 0) {
          const chatDoc = querySnapshot.docs[0];
          const chatData = chatDoc.data();
          
          // Only update state if values have actually changed
          if (chatData.status !== currentInquiryStatus) {
            setCurrentInquiryStatus(chatData.status);
          }
          if (chatData.ticketNumber && chatData.ticketNumber !== currentTicketNumber) {
            setCurrentTicketNumber(chatData.ticketNumber);
          }

          if (chatData.status === "active") {
            if (activeChatId !== chatDoc.id) {
              setActiveChatId(chatDoc.id);
            }
            if (chatMode !== "chat") {
              setChatMode("chat");
            }
            if (isWaitingForEducator) {
              setIsWaitingForEducator(false);
            }
            if (isWaitingInQueue) {
              setIsWaitingInQueue(false);
            }
          } else if (chatData.status === "assigned") {
            if (currentInquiryStatus !== "assigned") {
              setCurrentInquiryStatus("assigned");
            }
            if (activeChatId !== null) {
              setActiveChatId(null);
            }
            if (chatMode !== "waiting") {
              setChatMode("waiting");
            }
          }
        }
      },
      (error) => {
        console.error("Error listening for assigned chats:", error);
      }
    );

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [currentInquiryId]);

  // Load messages for waiting inquiry (before peer educator enters chat) - OPTIMIZED
  useEffect(() => {
    if (!currentInquiryId || activeChatId || currentInquiryStatus === "assigned") return;

    setMessagesLoading(true);
    setMessagesLoaded(false);

    let mounted = true;

    const messagesRef = collection(db, "inquiries", currentInquiryId, "messages");
    // Limit to last 200 messages to avoid loading entire message history for long conversations
    const q = query(
      messagesRef,
      orderBy("order", "asc"),
      orderBy("createdAt", "asc"),
      limit(200)
    );

    // Track which Firestore doc IDs are already in local state so we don't
    // duplicate messages that were appended locally during the onboarding flow.
    const seenDocIds = new Set();

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      if (!mounted) return;

      let maxOrder = -1;
      let hasNewMessages = false;

      querySnapshot.docChanges().forEach((change) => {
        if (change.type !== "added") return;

        const docSnap = change.doc;
        if (seenDocIds.has(docSnap.id)) return;
        seenDocIds.add(docSnap.id);

        const data = docSnap.data();
        if (data.order !== undefined && data.order > maxOrder) {
          maxOrder = data.order;
        }

        // Append message only if its text isn't already present in local state
        // (avoids duplicating locally-appended onboarding messages)
        setMessages((prev) => {
          if (prev.some((m) => m.id === docSnap.id || m.text === data.text)) return prev;
          const newMsg = {
            id: docSnap.id,
            sender: data.senderRole === "user" ? "user" : "auri",
            text: data.text,
            ...data,
          };
          return [...prev, newMsg].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        });

        hasNewMessages = true;
      });

      if (maxOrder >= 0) {
        syncMessageOrderFromMessages([{ order: maxOrder }]);
      }

      setMessagesLoading(false);
      setMessagesLoaded(true);
    }, (error) => {
      console.error("Error loading inquiry messages:", error);
      setMessagesLoading(false);
      setMessagesLoaded(true);
    });

    return () => {
      mounted = false;
      unsubscribe();
      setMessagesLoading(false);
    };
  }, [currentInquiryId, activeChatId, currentInquiryStatus]);

  // Listen for chat messages when in active chat mode - OPTIMIZED
  useEffect(() => {
    if (!activeChatId) return;

    setMessagesLoading(true);
    setMessagesLoaded(false);

    let mounted = true;

    const messagesRef = collection(db, "chats", activeChatId, "messages");
    // Limit to last 200 messages to avoid loading entire chat history for long conversations
    const q = query(
      messagesRef,
      orderBy("order", "asc"),
      orderBy("createdAt", "asc"),
      limit(200)
    );

    const unsubscribe = onSnapshot(q, 
      (querySnapshot) => {
        if (!mounted) return;
        
        const chatMessagesData = [];
        let maxOrder = -1;
        let hasOnboardingMessages = false;
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          chatMessagesData.push({
            id: doc.id,
            sender: data.senderRole === "user" ? "user" : "auri",
            text: data.text,
            ...data
          });
          if (data.order !== undefined && data.order > maxOrder) {
            maxOrder = data.order;
          }
          // Check if this is an onboarding message
          if (data.senderRole === "system" && (
            data.text.includes("Hi there! You're about to chat") ||
            data.text.includes("Great! Before we continue") ||
            data.text.includes("Excellent! To help us better assist you") ||
            data.text.includes("Thank you for sharing") ||
            data.text.includes("Your inquiry has been submitted")
          )) {
            hasOnboardingMessages = true;
          }
        });
        
        syncMessageOrderFromMessages(chatMessagesData);
        
        // Set onboarding completed flag if onboarding messages exist
        setOnboardingCompleted(hasOnboardingMessages);
        
        // Filter out messages that are already in preface (to avoid duplicates)
        const prefaceTexts = prefaceMessagesRef.current.map(msg => msg.text);
        const filteredChatMessages = chatMessagesData.filter(msg => 
          !prefaceTexts.includes(msg.text)
        );
        
        // Check if there are peer educator messages and enable input field if so
        const hasPeerEducatorMessages = filteredChatMessages.some(msg => msg.sender === "auri");
        if (hasPeerEducatorMessages && isWaitingForEducator) {
          setIsWaitingForEducator(false);
        }
        
        // Combine preface messages with filtered chat messages
        // Batch state updates to reduce re-renders
        setMessages([...prefaceMessagesRef.current, ...filteredChatMessages]);
        setMessagesLoading(false);
        setMessagesLoaded(true);
      },
      (error) => {
        console.error("Error loading chat messages:", error);
        setMessagesLoading(false);
        setMessagesLoaded(true);
      }
    );

    return () => {
      mounted = false;
      unsubscribe();
      setMessagesLoading(false);
    };
  }, [activeChatId]);

  // Listen for chat status changes to detect escalation and resolution - OPTIMIZED
  useEffect(() => {
    if (!activeChatId) {
      setIsChatEscalated(false);
      setEscalationStatus(null);
      currentChatStatusRef.current = null;
      currentChatEscalationStatusRef.current = null;
      return;
    }

    let mounted = true;

    const chatRef = doc(db, "chats", activeChatId);
    const unsubscribe = onSnapshot(chatRef, (docSnapshot) => {
      if (!mounted) return;
      
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        const isEscalated = data.status === 'escalated';
        // Track in ref for use in sendMessage (eliminates getDoc read)
        currentChatStatusRef.current = data.status;
        currentChatEscalationStatusRef.current = data.escalationStatus;
        setIsChatEscalated(isEscalated);
        setEscalationStatus(data.escalationStatus);
      }
    }, (error) => {
      console.error("Error listening to chat status:", error);
    });

    return () => {
      mounted = false;
      currentChatStatusRef.current = null;
      currentChatEscalationStatusRef.current = null;
      unsubscribe();
    };
  }, [activeChatId]);

  // Format inquiry time for display
  const formatInquiryTime = (timestamp) => {
    if (!timestamp) return "Just now";
    
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }
    
    if (isNaN(date.getTime())) return "Just now";
    
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return `Just now`;
    if (diffMins < 60) return `${diffMins} mins`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''}`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
    return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
  };

  // Auto-scroll
  useEffect(() => {
    if (!messagesScrollRef.current) return;
    requestAnimationFrame(() => {
      const el = messagesScrollRef.current;
      if (el) {
        el.scrollTop = el.scrollHeight + 100;
      }
    });
  }, [messages, isUserTyping, isSupportTyping, started]);

  // Typing handler
  const handleTyping = (text) => {
    setNewMessage(text);
    
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      if (textareaRef.current.scrollHeight) {
        textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
      }
    }

    if (!isUserTyping) setIsUserTyping(true);
    
    // Clear typing indicator after 2 seconds of no typing
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      setIsUserTyping(false);
    }, 2000);
  };

  // Handle sending message
  const handleSendMessage = async () => {
    const trimmedMessage = newMessage.trim();
    if (!trimmedMessage) return;

    // Validate message for profanity
    const validation = validateMessage(trimmedMessage);
    if (!validation.isValid) {
      setShowProfanityModal(true);
      return;
    }

    // Check authentication state before sending
    if (!isAuthenticated) {
      alert("You must be logged in to send messages");
      return;
    }

    // Add user message to UI immediately for better UX
    // Only add to UI if no chat exists, otherwise Firestore listener will handle it
    if (!activeChatId) {
      const order = allocateMessageOrder();
      const userMsg = { sender: "user", text: trimmedMessage, createdAt: new Date(), order };
      setMessages((prev) => [...prev, userMsg]);
    }
    setNewMessage("");
    
    // Store message to Firestore if chat exists
    if (activeChatId) {
      try {
        // Check authentication before attempting to send
        const userId = auth.currentUser?.uid;
        if (!userId) {
          console.error("User not authenticated - cannot send message");
          return;
        }

        // Use local order counter to avoid transaction permission issues
        const currentOrder = allocateMessageOrder();

        // Write message directly without transaction to avoid permission issues
        const msgRef = doc(collection(db, "chats", activeChatId, "messages"));
        await setDoc(msgRef, {
          senderId: userId,
          senderRole: "user",
          text: trimmedMessage,
          createdAt: serverTimestamp(),
          order: currentOrder
        });

        // Try to update chat document - don't fail if this has permission issues
        try {
          const chatRef = doc(db, "chats", activeChatId);
          await updateDoc(chatRef, {
            nextMessageOrder: currentOrder + 1,
            lastMessage: trimmedMessage,
            lastActivityAt: serverTimestamp(),
            userLastActivityAt: serverTimestamp(),
            lastMessageSender: "user"
          });
        } catch (updateError) {
          console.warn("Chat document update failed (message was saved):", updateError);
          // Don't alert user - message was successfully saved
        }
      } catch (error) {
        console.error("Error storing message to Firestore:", error);
        alert("Failed to send message: " + error.message);
      }
    } else if (currentInquiryId && !activeChatId) {
      try {
        // Check authentication before attempting to send
        const userId = auth.currentUser?.uid;
        if (!userId) {
          console.error("User not authenticated - cannot send message");
          alert("You must be logged in to send messages");
          return;
        }

        const currentOrder = allocateMessageOrder();
        const batch = writeBatch(db);
        
        // Add message to batch
        const messagesRef = collection(db, "inquiries", currentInquiryId, "messages");
        const newMessageRef = doc(messagesRef);
        batch.set(newMessageRef, {
          senderId: userId,
          senderRole: "user",
          text: trimmedMessage,
          order: currentOrder,
          createdAt: serverTimestamp()
        });
        
        // Update inquiry document in same batch
        const inquiryRef = doc(db, "inquiries", currentInquiryId);
        batch.update(inquiryRef, {
          messagePreview: trimmedMessage,
          lastMessage: trimmedMessage,
          lastActivityAt: serverTimestamp(),
          userLastActivityAt: serverTimestamp()
        });
        
        // Commit batch (single write operation instead of two separate writes)
        await batch.commit();
      } catch (error) {
        console.error("Error updating inquiry with message:", error);
        alert("Failed to send message: " + error.message);
      }
    }
    
    // If this is the first message after preface or we have an existing inquiry to update
    // Skip this if we're already in chat mode
    if (!userInQueue && disclaimerConfirmed && chatMode !== "chat") {
      try {
        if (currentInquiryId) {
          setUserInQueue(true);
          setIsWaitingForEducator(true);
          setIsWaitingInQueue(true);

          if (!isChatEscalated) {
            setTimeout(() => {
              setIsSupportTyping(true);
              setTimeout(async () => {
                setIsSupportTyping(false);
                await addWaitingSubmissionMessage(currentInquiryId, currentTicketNumber);
                await updateInquiryMetadata(currentInquiryId, {
                  messagePreview: trimmedMessage,
                  userLastActivityAt: serverTimestamp(),
                  lastMessage: trimmedMessage,
                  status: "waiting",
                  messagesReady: true
                });
                setInquirySubmittedAt(Date.now());
              }, 1500);
            }, 500);
          } else {
            await updateInquiryMetadata(currentInquiryId, {
              messagePreview: trimmedMessage,
              userLastActivityAt: serverTimestamp(),
              lastMessage: trimmedMessage,
              status: "waiting",
              messagesReady: true
            });
            setInquirySubmittedAt(Date.now());
          }
        } else {
          // No existing inquiry - create new one after showing typing indicator
          setUserInQueue(true);
          setIsWaitingForEducator(true);

          const userOrder = messageOrderRef.current - 1;
          const msgsToSave = [...messages, { sender: "user", text: trimmedMessage, order: userOrder }];

          if (!isChatEscalated) {
            setTimeout(() => {
              setIsSupportTyping(true);
              setTimeout(async () => {
                setIsSupportTyping(false);
                await createInquiry(trimmedMessage, msgsToSave);
              }, 1500);
            }, 500);
          } else {
            await createInquiry(trimmedMessage, msgsToSave);
          }
        }
      } catch (error) {
        console.error("Failed to handle inquiry:", error);
        // Show error message to user
        setTimeout(() => {
          setIsSupportTyping(true);
          setTimeout(() => {
            setIsSupportTyping(false);
            const currentOrder = allocateMessageOrder();
            setMessages((prev) => [
              ...prev,
              {
                sender: "auri",
                text: "Sorry, there was an error submitting your inquiry. Please try again.",
                createdAt: new Date(),
                order: currentOrder
              },
            ]);
          }, 1500);
        }, 500);
      }
      return;
    }

    // If already in chat mode, send message to existing chat
    if (chatMode === "chat" && activeChatId) {
      try {
        // Use tracked chat status from listener instead of getDoc read
        const currentChatStatus = currentChatStatusRef.current;

        // Update last message in chat document and status if needed
        try {
          const chatUpdateData = {
            lastMessage: trimmedMessage,
            updatedAt: serverTimestamp(),
            lastActivityAt: serverTimestamp(),
            userLastActivityAt: serverTimestamp(),
            lastMessageSender: "user"
          };
          
          // If chat is inactive, update to standby and set high priority
          if (currentChatStatus === "inactive") {
            chatUpdateData.status = "standby";
            chatUpdateData.isHighPriority = true;
          }
          
          await updateDoc(doc(db, "chats", activeChatId), chatUpdateData);
        } catch (chatUpdateError) {
          console.warn("Could not update chat metadata (likely permission issue):", chatUpdateError);
        }
      } catch (error) {
        console.error("Error sending message to chat:", error);
      }
    }
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    
    // Reset chat based on tab
    if (tab === "archived") {
      // Check if there are any archived chats
      const archivedChats = chats.filter(chat => chat.archived);
      
      if (archivedChats.length === 0) {
        // No archived chats exist - show message and reset state
        setSelectedChat(null);
        setStarted(false);
        setDisclaimerConfirmed(false);
        setUserInQueue(false);
        setIsWaitingForEducator(false);
        setCurrentInquiryId(null);
        setActiveChatId(null);
        setChatMode("waiting");
        setCurrentInquiryStatus(null);
        setCurrentTicketNumber(null);
        setHasAddedInitialWaitingMessage(false);
        setHasAddedAssignedMessage(false);
        setIsWaitingInQueue(false);
        setOriginalPreferredLanguage(null);
        setInquirySubmittedAt(null);
        setIsChatEscalated(false);
        setEscalationStatus(null);
        setMessages([{ sender: "system", text: "No archived chats." }]);
        setPrefaceMessages([{ sender: "system", text: "No archived chats." }]);
        prefaceMessagesRef.current = [{ sender: "system", text: "No archived chats." }];
        setNewMessage("");
        setIsUserTyping(false);
        setIsSupportTyping(false);
        // Clear onboarding message cache when switching contexts
        persistedOnboardingMessages.current.clear();
      } else {
        // Archived chats exist - load the latest one
        const latestArchivedChat = archivedChats[0];
        setSelectedChat(latestArchivedChat);
        setStarted(true);
        setMessages([]);
        setNewMessage("");
        setIsUserTyping(false);
        setIsSupportTyping(false);
        setCurrentInquiryStatus(null);
        setCurrentTicketNumber(null);
        setHasAddedInitialWaitingMessage(false);
        setHasAddedAssignedMessage(false);
        setIsWaitingInQueue(false);
        setOriginalPreferredLanguage(null);
        setInquirySubmittedAt(null);
        
        // Load the archived chat messages for viewing
        if (latestArchivedChat.source === 'inquiry') {
          setCurrentInquiryId(latestArchivedChat.inquiryId);
          setChatMode("chat");
          setDisclaimerConfirmed(true);
        } else if (latestArchivedChat.source === 'chat') {
          setActiveChatId(latestArchivedChat.id);
          setChatMode("chat");
          setDisclaimerConfirmed(true);
        }
      }
    } else {
      // Open new chat for "open" tab
      setSelectedChat(null);
      setStarted(false);
      setDisclaimerConfirmed(false);
      setUserInQueue(false);
      setIsWaitingForEducator(false);
      setCurrentInquiryId(null);
      setActiveChatId(null);
      setChatMode("waiting");
      setCurrentInquiryStatus(null);
      setCurrentTicketNumber(null);
      setHasAddedInitialWaitingMessage(false);
      setHasAddedAssignedMessage(false);
      setIsWaitingInQueue(false);
      setOriginalPreferredLanguage(null);
      setInquirySubmittedAt(null);
      setIsChatEscalated(false);
      setEscalationStatus(null);
      const initialMessages = [
        { sender: "auri", text: "Hi there! You're about to chat with an AuraVit Peer Educator.", order: 0 }
      ];
      messageOrderRef.current = 1;
      setNextMessageOrder(1);
      setPrefaceMessages(initialMessages);
      prefaceMessagesRef.current = initialMessages;
      setMessages(initialMessages);
      setNewMessage("");
      setIsUserTyping(false);
      setIsSupportTyping(false);
      // Clear onboarding message cache when starting new chat
      persistedOnboardingMessages.current.clear();
    }
  };

  // Handle archiving a chat - show confirmation modal
  const handleArchiveChat = (e, chat) => {
    if (e) e.stopPropagation();
    setChatToArchive(chat);
    setShowArchiveModal(true);
  };

  // Confirm archive - update Firestore status
  const confirmArchiveChat = async () => {
    if (!chatToArchive) return;

    try {
      // Update the inquiry status to "resolved" (which means archived in this context)
      if (chatToArchive.source === 'inquiry') {
        await updateDoc(doc(db, "inquiries", chatToArchive.inquiryId), {
          status: "resolved",
          lastActivityAt: serverTimestamp()
        });
      } else if (chatToArchive.source === 'chat') {
        // For chat documents, update the status in the chats collection
        await updateDoc(doc(db, "chats", chatToArchive.chatId || chatToArchive.id), {
          status: "resolved",
          lastActivityAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error("Error archiving chat:", error);
    }

    setShowArchiveModal(false);
    setChatToArchive(null);

    // Update local state to remove from open tab
    setChats(prevChats => prevChats.filter(chat => chat.id !== chatToArchive.id));

    // If the archived chat was selected, deselect it
    if (selectedChat?.id === chatToArchive.id) {
      setSelectedChat(null);
      setActiveChatId(null);
      setMessages([
        { sender: "auri", text: "Hi there! You're about to chat with an AuraVit Peer Educator.", order: 0 }
      ]);
      setPrefaceMessages([
        { sender: "auri", text: "Hi there! You're about to chat with an AuraVit Peer Educator.", order: 0 }
      ]);
      prefaceMessagesRef.current = [
        { sender: "auri", text: "Hi there! You're about to chat with an AuraVit Peer Educator.", order: 0 }
      ];
      setStarted(false);
      setChatMode("waiting");
    }
  };

  // Cancel archive
  const cancelArchiveChat = () => {
    setShowArchiveModal(false);
    setChatToArchive(null);
  };

  // Handle opening archived chat - show confirmation modal
  const handleOpenChat = (e, chat) => {
    e.stopPropagation();
    setChatToOpen(chat);
    setShowOpenModal(true);
  };

  // Confirm opening archived chat - update Firestore status
  const confirmOpenChat = async () => {
    if (!chatToOpen) return;

    try {
      // Update the inquiry status back to "active"
      if (chatToOpen.source === 'inquiry') {
        await updateDoc(doc(db, "inquiries", chatToOpen.inquiryId), {
          status: "active",
          lastActivityAt: serverTimestamp()
        });
      } else if (chatToOpen.source === 'chat') {
        // For chat documents, update the status in the chats collection
        await updateDoc(doc(db, "chats", chatToOpen.id), {
          status: "active",
          lastActivityAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error("Error opening chat:", error);
    }

    setShowOpenModal(false);
    setChatToOpen(null);
  };

  // Cancel opening archived chat
  const cancelOpenChat = () => {
    setShowOpenModal(false);
    setChatToOpen(null);
  };

  // Handle deleting a chat - show confirmation modal
  const handleDeleteChat = (e, chat) => {
    if (e) e.stopPropagation();
    setChatToDelete(chat);
    setShowDeleteModal(true);
  };

  // Confirm delete - delete inquiry document
  const confirmDeleteChat = async () => {
    if (!chatToDelete) return;

    try {
      // Delete the inquiry document from Firestore
      if (chatToDelete.source === 'inquiry' && chatToDelete.inquiryId) {
        await deleteDoc(doc(db, "inquiries", chatToDelete.inquiryId));
      } else if (chatToDelete.source === 'chat') {
        // For chat documents, try to delete using the correct ID
        const chatIdToDelete = chatToDelete.chatId || chatToDelete.id;
        if (chatIdToDelete) {
          try {
            await deleteDoc(doc(db, "chats", chatIdToDelete));
          } catch (deleteError) {
            console.error("Error deleting chat document:", deleteError);
            // If delete fails due to permissions, try updating status to deleted
            try {
              await updateDoc(doc(db, "chats", chatIdToDelete), {
                status: "deleted",
                lastActivityAt: serverTimestamp()
              });
            } catch (updateError) {
              console.error("Error updating chat status to deleted:", updateError);
              throw deleteError;
            }
          }
        }
      }
    } catch (error) {
      console.error("Error deleting chat:", error);
      // Show error to user
      alert("Error deleting chat: " + error.message);
      return;
    }

    setShowDeleteModal(false);
    setChatToDelete(null);

    // Update local state to remove from open tab
    setChats(prevChats => prevChats.filter(chat => chat.id !== chatToDelete.id));

    // If the deleted chat was selected, deselect it
    if (selectedChat?.id === chatToDelete.id) {
      setSelectedChat(null);
      setActiveChatId(null);
      setMessages([
        { sender: "auri", text: "Hi there! You're about to chat with an AuraVit Peer Educator.", order: 0 }
      ]);
      setPrefaceMessages([
        { sender: "auri", text: "Hi there! You're about to chat with an AuraVit Peer Educator.", order: 0 }
      ]);
      prefaceMessagesRef.current = [
        { sender: "auri", text: "Hi there! You're about to chat with an AuraVit Peer Educator.", order: 0 }
      ];
      setStarted(false);
      setChatMode("waiting");
    }
  };

  // Cancel delete
  const cancelDeleteChat = () => {
    setShowDeleteModal(false);
    setChatToDelete(null);
  };

  // Filter chats based on active tab and search
  const filteredChats = chats.filter((chat) =>
    activeTab === "archived" ? chat.archived : !chat.archived
  ).filter(chat => 
    searchQuery === "" || 
    chat.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <section className="human-support-root">
      {/* Workspace */}
      <div className="askai-workspace">

        {/* Mobile sidebar backdrop */}
        {isMobileSidebarOpen && (
          <div
            className="askai-sidebar-backdrop visible"
            onClick={() => setIsMobileSidebarOpen(false)}
          />
        )}

        {/* Sidebar Navigation */}
        <aside className={`askai-sidebar ${sidebarCollapsed ? "collapsed" : ""} ${isMobileSidebarOpen ? "mobile-open" : ""}`}>
          {/* Glass Container for Logo and Toggle Button */}
          <div
            style={{
              background: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(16px)',
              borderRadius: '20px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              padding: '16px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: sidebarCollapsed ? 'center' : 'space-between',
              gap: '12px',
              position: 'relative'
            }}
          >
            {/* Logo - clickable to open sidebar when collapsed */}
            <div 
              style={{ 
                display: "flex", 
                alignItems: "center", 
                gap: "12px",
                cursor: sidebarCollapsed ? 'pointer' : 'default'
              }}
              onClick={() => {
                if (sidebarCollapsed) {
                  setSidebarCollapsed(false);
                }
              }}
            >
              <FaHeadset size={32} style={{ color: "#ffffff" }} />
              {!sidebarCollapsed && (
                <h1 style={{ 
                  fontSize: "24px", 
                  margin: "0",
                  color: "#ffffff"
                }}>{t.humanSupport}</h1>
              )}
            </div>

            {/* Toggle Button - only visible when sidebar is open */}
            {!sidebarCollapsed && (
              <button
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '12px',
                  background: 'transparent',
                  color: '#ffffff',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  transition: 'background 0.2s ease'
                }}
                onClick={() => {
                  // On mobile, close the drawer instead of collapsing
                  if (window.innerWidth < 768) {
                    setIsMobileSidebarOpen(false);
                  } else {
                    setSidebarCollapsed(true);
                  }
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <FiMenu />
              </button>
            )}
          </div>
          
          {/* Search Bar */}
          {!sidebarCollapsed && (
            <div className="askai-search-box">
              <div className="askai-search-box-inner">
                <FiSearch className="askai-search-icon" />
                <input
                  type="text"
                  placeholder={t.searchChats}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          )}
          
          {/* New Chat Button */}
          {activeTab === "open" && (
            <button
              className="askai-new-chat-btn"
              onClick={() => {
                setSelectedChat(null);
                setStarted(false);
                setDisclaimerConfirmed(false);
                setUserInQueue(false);
                setIsWaitingForEducator(false);
                setCurrentInquiryId(null);
                setActiveChatId(null);
                setChatMode("waiting");
                setCurrentInquiryStatus(null);
                setCurrentTicketNumber(null);
                setHasAddedInitialWaitingMessage(false);
                setHasAddedAssignedMessage(false);
                setIsWaitingInQueue(false);
                setOriginalPreferredLanguage(null);
                setInquirySubmittedAt(null);
                setIsChatEscalated(false);
                setEscalationStatus(null);
                const initialMessages = [
                  { sender: "auri", text: "Hi there! You're about to chat with an AuraVit Peer Educator.", order: 0 }
                ];
                messageOrderRef.current = 1;
                setNextMessageOrder(1);
                setPrefaceMessages(initialMessages);
                prefaceMessagesRef.current = initialMessages;
                setMessages(initialMessages);
                setNewMessage("");
                setIsUserTyping(false);
                setIsSupportTyping(false);
                // Clear onboarding message cache when starting new chat
                persistedOnboardingMessages.current.clear();
              }}
            >
              <TbMessagePlus /> {!sidebarCollapsed && t.newChat}
            </button>
          )}
          
          {/* Open/Archived Tabs in Sidebar */}
          <div className="askai-sidebar-tabs">
            <button
              className={`askai-sidebar-tab-btn ${activeTab === "open" ? "active" : ""}`}
              onClick={() => handleTabChange("open")}
            >
              <TbMessages /> {!sidebarCollapsed && t.open}
            </button>
            <button
              className={`askai-sidebar-tab-btn ${activeTab === "archived" ? "active" : ""}`}
              onClick={() => handleTabChange("archived")}
            >
              <FiArchive /> {!sidebarCollapsed && t.archived}
            </button>
          </div>
          
          {/* Chat History */}
          <div className="askai-chat-history">
            {!sidebarCollapsed && <h3>Chats</h3>}
            <div className="askai-chat-list">
              {chatsLoading && !chatsLoaded ? (
                // Skeleton loading for chat list
                [...Array(5)].map((_, i) => (
                  <div key={i} className="askai-chat-item skeleton-chat-item">
                    <div className="askai-chat-item-content">
                      <div className="skeleton skeleton-chat-title"></div>
                      <div className="skeleton skeleton-chat-time"></div>
                    </div>
                  </div>
                ))
              ) : filteredChats.length === 0 ? (
                <div className="askai-empty-state">
                  <p>{activeTab === "archived" ? "No archived chats" : "No active conversations"}</p>
                </div>
              ) : (
                filteredChats.map((chat, i) => (
                  <div
                    key={i}
                    className={`askai-chat-item ${selectedChat?.title === chat.title ? "active" : ""}`}
                    onClick={async () => {
                      setSelectedChat(chat);
                      setStarted(true);
                      
                      // Handle both chats and inquiries
                      if (chat.source === 'chat' && chat.chatId) {
                        // This is a chat with messages in the chats collection
                        setActiveChatId(chat.chatId);
                        setChatMode("chat"); // Set to chat mode even for inactive/standby chats
                        setCurrentInquiryId(chat.inquiryId || chat.id);
                        
                        // Reset escalation status - listener will populate it correctly
                        // This prevents showing stale data from previous chat
                        setIsChatEscalated(false);
                        setEscalationStatus(null);
                        
                        // Clear preface messages - all messages will be loaded from Firestore
                        setPrefaceMessages([]);
                        prefaceMessagesRef.current = [];
                        setMessages([]);
                        
                        setNewMessage("");
                        setIsUserTyping(false);
                        setIsSupportTyping(false);
                        // Close mobile drawer when chat is selected
                        setIsMobileSidebarOpen(false);
                        return;
                      } else if (chat.source === 'inquiry') {
                        setActiveChatId(null);
                        setChatMode("waiting");
                        setCurrentInquiryId(chat.id);
                        setCurrentTicketNumber(chat.ticketNumber);
                        setCurrentInquiryStatus("waiting");
                        
                        setPrefaceMessages([]);
                        prefaceMessagesRef.current = [];
                        setMessages([]);
                        setNewMessage("");
                        setIsUserTyping(false);
                        setIsSupportTyping(false);
                        setUserInQueue(true);
                        setIsWaitingForEducator(true);
                        setDisclaimerConfirmed(true);
                        setStarted(true);
                        // Close mobile drawer when chat is selected
                        setIsMobileSidebarOpen(false);
                        return;
                      }
                    }}
                  title={chat.title}>
                    {!sidebarCollapsed && (
                      <div className="askai-chat-item-content">
                        {/* Status Badge */}
                        {getStatusBadge(chat) && (
                          <div 
                            style={{
                              position: 'absolute',
                              bottom: '8px',
                              right: '8px',
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              backgroundColor: getStatusBadge(chat).bgColor,
                              color: getStatusBadge(chat).color,
                              zIndex: 1
                            }}
                          >
                            {getStatusBadge(chat).text}
                          </div>
                        )}
                        <div className="askai-chat-item-title">
                          {chat.inquiryTopic ? (
                            <>
                              <div style={{ fontWeight: "bold" }}>{chat.inquiryTopic}</div>
                              <div style={{ 
                                fontSize: "12px", 
                                color: "white", 
                                marginTop: "2px", 
                                whiteSpace: "nowrap", 
                                overflow: "hidden", 
                                textOverflow: "ellipsis",
                                maxWidth: "200px"
                              }}>
                                {chat.messagePreview?.length > 30 ? chat.messagePreview.substring(0, 30) + "..." : chat.messagePreview}
                              </div>
                            </>
                          ) : (
                            chat.title
                          )}
                        </div>
                        <div className="askai-chat-item-time">{chat.time}</div>
                      </div>
                    )}
                    {!sidebarCollapsed && activeTab === "open" && chat.status === "escalated" && chat.escalationStatus === "resolved" && (
                      <>
                        <button
                          className="askai-chat-item-archive"
                          onClick={(e) => handleArchiveChat(e, chat)}
                          title="Archive chat"
                        >
                          <FiArchive />
                        </button>
                      </>
                    )}
                    {!sidebarCollapsed && activeTab === "open" && chat.status === "waiting" && (
                      <button
                        className="askai-chat-item-archive"
                        onClick={(e) => handleDeleteChat(e, chat)}
                        title="Delete chat"
                      >
                        <FiTrash2 />
                      </button>
                    )}
                    {!sidebarCollapsed && activeTab === "open" && chat.status === "inactive" && (
                      <button
                        className="askai-chat-item-archive"
                        onClick={(e) => handleDeleteChat(e, chat)}
                        title="Delete chat"
                      >
                        <FiTrash2 />
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="askai-main">
          <div className="conversation-panel">
            {/* Mobile hamburger bar – visible only on mobile via CSS */}
            <div className="askai-mobile-topbar" style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', borderBottom: '1px solid #e5e7eb', background: '#fff' }}>
              <button
                className="askai-mobile-sidebar-btn"
                onClick={() => setIsMobileSidebarOpen(true)}
                aria-label="Open chat history"
                aria-expanded={isMobileSidebarOpen}
              >
                <FiMenu />
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaHeadset size={28} style={{ color: '#3b82f6' }} />
                <span style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b' }}>Human Support</span>
              </div>
            </div>
            {/* Scrollable messages */}
            <div className="messages-scroll" ref={messagesScrollRef}>
              {messagesLoading && !messagesLoaded ? (
                // Skeleton loading for conversation panel
                [...Array(3)].map((_, i) => (
                  <div key={i} className="chat-message skeleton-message">
                    <div className="skeleton skeleton-message-avatar"></div>
                    <div className="skeleton skeleton-message-text"></div>
                    <div className="skeleton skeleton-message-text"></div>
                    <div className="skeleton skeleton-message-text" style={{ width: '60%' }}></div>
                  </div>
                ))
              ) : messages.length === 0 && selectedChat ? (
                <div className="askai-empty-messages">
                  <p>No messages in this conversation yet</p>
                </div>
              ) : (
                messages.map((msg, i) => (
                  msg.isOfficeHours ? (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '40px 20px',
                        margin: '20px 0'
                      }}
                    >
                      <div style={{
                        padding: '24px 32px',
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        borderRadius: '16px',
                        color: '#ef4444',
                        fontSize: '15px',
                        fontWeight: '600',
                        textAlign: 'center',
                        lineHeight: '1.6',
                        maxWidth: '500px'
                      }}>
                        <div style={{ marginBottom: '8px' }}>{msg.text}</div>
                        <div style={{ fontSize: '13px', fontWeight: '500', color: '#dc2626', whiteSpace: 'pre-line' }}>
                          {msg.subtext}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      key={i}
                      className={`chat-message ${msg.sender === "user" ? "user" : "auri"}`}
                    >
                      {msg.sender === "auri" && (
                        <div className="peer-educator-label">
                          {msg.senderName || msg.isResolvedMessage || msg.senderRole === "medical professional"
                            ? "Medical Professional"
                            : msg.isEscalationNotice || msg.senderRole === "system"
                              ? (selectedChat?.peerEducatorId
                                ? `Peer Educator - (${peerEducatorNames[selectedChat.peerEducatorId] || "Peer Educator"})`
                                : "Peer Educator")
                              : selectedChat?.peerEducatorId
                              ? `Peer Educator - (${peerEducatorNames[selectedChat.peerEducatorId] || "Peer Educator"})`
                              : "Peer Educator"
                          }
                        </div>
                      )}
                      {msg.sender === "user" && (
                        <div className="peer-educator-label">You</div>
                      )}
                      <div className="message-content">
                        <div className="message-text" style={{ whiteSpace: 'pre-line' }}>{msg.text}</div>
                        {msg.createdAt && (
                          <div className="message-time">
                            {formatTimestamp(msg.createdAt)}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                ))
              )}



              {showLanguageOptions && !selectedChat && activeTab !== "archived" && (
                <div className="chat-message auri">
                  <div className="peer-educator-label">Peer Educator</div>
                  <div className="message-content">
                    <div className="language-options-container">
                      <div className="language-options">
                        {availableLanguages.map((lang) => {
                          let label;
                          if (lang === "english") label = t.english;
                          else if (lang === "tagalog") label = t.tagalog;
                          else if (lang === "bisaya") label = t.bisaya;
                          else label = lang.charAt(0).toUpperCase() + lang.slice(1);
                          
                          return (
                            <button
                              key={lang}
                              className={`language-option ${selectedLanguage === lang ? "selected" : ""}`}
                              onClick={() => handleLanguageSelect(lang)}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {showTopicOptions && !selectedChat && activeTab !== "archived" && (
                <div className="chat-message auri">
                  <div className="peer-educator-label">Peer Educator</div>
                  <div className="message-content">
                    <div className="language-options-container">
                      <div className="language-options">
                        {[
                          t.hivAwareness,
                          t.hivScreeningTesting,
                          t.birthControlImplant,
                          t.contraception,
                          t.familyPlanning,
                          t.genderBasedViolence,
                          t.menstrualHealthHygiene,
                          t.other
                        ].map((topic) => (
                          <button
                            key={topic}
                            className={`language-option ${selectedTopic === topic ? "selected" : ""}`}
                            onClick={() => handleTopicSelect(topic)}
                          >
                            {topic}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {isUserTyping && (
                <div className="chat-message user typing">
                  <div className="message-text typing-text">
                    <span className="typing-dot">.</span>
                    <span className="typing-dot">.</span>
                    <span className="typing-dot">.</span>
                  </div>
                </div>
              )}

              {isSupportTyping && (
                <div className="chat-message auri typing">
                  <div className="peer-educator-label">
                    {selectedChat?.peerEducatorId 
                      ? `Peer Educator - (${peerEducatorNames[selectedChat.peerEducatorId] || "Peer Educator"})`
                      : "Peer Educator"
                    }
                  </div>
                  <div className="message-text typing-text">
                    <span className="typing-dot">.</span>
                    <span className="typing-dot">.</span>
                    <span className="typing-dot">.</span>
                  </div>
                </div>
              )}

              <div ref={messagesScrollRef} />
            </div>

            {/* Input area */}
            <div className="chat-input-area">
              {!selectedChat && !disclaimerConfirmed && activeTab !== "archived" && isOutsideOfficeHours ? (
                <button
                  className="lets-started-btn-full"
                  onClick={onBack}
                >
                  Ask Auri
                </button>
              ) : (
                <>
                  {/* Ask Auri Button */}
                  <button
                    className="headset-btn"
                    onClick={onBack}
                    title="Ask Auri"
                  >
                    <img src={auriImg} alt="Auri" style={{ width: "20px", height: "20px", borderRadius: "0" }} />
                  </button>

                  {!selectedChat && !disclaimerConfirmed && activeTab !== "archived" && !isOutsideOfficeHours && (
                    <button
                      className="lets-started-btn-full"
                      onClick={handleIUnderstand}
                    >
                      I Understand
                    </button>
                  )}

                  {(!selectedChat && !disclaimerConfirmed && activeTab !== "archived") ? null : (
                    <>
                      <textarea
                        ref={textareaRef}
                        placeholder={
                          activeChatId && escalationStatus === 'resolved'
                            ? "This conversation is now closed. If you need further assistance, you may start a new inquiry."
                            : activeChatId && isChatEscalated
                              ? "This conversation is currently under review by a Medical Professional. You can view your conversation history while waiting for their feedback."
                              : isWaitingForEducator
                                ? t.waitingForPeerEducator
                                : t.typeYourMessage
                        }
                        value={newMessage}
                        onChange={(e) => handleTyping(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                        disabled={!started || (selectedChat && selectedChat.archived) || isWaitingForEducator || (activeChatId && isChatEscalated)}
                        style={{
                          cursor: (!started || (selectedChat && selectedChat.archived) || isWaitingForEducator || (activeChatId && isChatEscalated)) ? "not-allowed" : "text"
                        }}
                        rows={1}
                      />

                      <button
                        className="send-btn"
                        onClick={handleSendMessage}
                        disabled={!started || (selectedChat && selectedChat.archived) || isWaitingForEducator || (activeChatId && isChatEscalated)}
                        style={{
                          opacity: (!started || (selectedChat && selectedChat.archived) || isWaitingForEducator || (activeChatId && isChatEscalated)) ? 0.5 : 1
                        }}
                      >
                        <FiArrowUp />
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Archive Confirmation Modal */}
      {showArchiveModal && (
        <div className="modal-overlay">
          <div className="modal-content archive-modal">
            <div className="archive-modal-header">
              <h3>Archive Chat</h3>
            </div>
            <div className="archive-modal-body">
              <p className="modal-description">This chat will be moved to your archived chats and will no longer appear in your active chats list.</p>
              <p>Are you sure you want to archive "{chatToArchive?.title}"?</p>
            </div>
            <div className="archive-modal-footer">
              <button className="modal-btn cancel" onClick={cancelArchiveChat}>
                Cancel
              </button>
              <button className="modal-btn confirm archive-btn" onClick={confirmArchiveChat}>
                Archive
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Open Chat Confirmation Modal */}
      {showOpenModal && (
        <div className="modal-overlay">
          <div className="modal-content open-modal">
            <div className="open-modal-header">
              <h3>Open Chat</h3>
            </div>
            <div className="open-modal-body">
              <p className="modal-description">This chat will be moved back to your active chats list and will appear in your open chats.</p>
              <p>Are you sure you want to open "{chatToOpen?.title}"?</p>
            </div>
            <div className="open-modal-footer">
              <button className="modal-btn cancel" onClick={cancelOpenChat}>
                Cancel
              </button>
              <button className="modal-btn confirm open-btn" onClick={confirmOpenChat}>
                Open
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-content archive-modal">
            <div className="archive-modal-header">
              <h3>Delete Chat</h3>
            </div>
            <div className="archive-modal-body">
              <p className="modal-description">This will permanently delete this inquiry and cannot be undone.</p>
              <p>Are you sure you want to delete "{chatToDelete?.title}"?</p>
            </div>
            <div className="archive-modal-footer">
              <button className="modal-btn cancel" onClick={cancelDeleteChat}>
                Cancel
              </button>
              <button className="modal-btn confirm archive-btn" onClick={confirmDeleteChat}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profanity Modal */}
      <ProfanityModal 
        isOpen={showProfanityModal} 
        onClose={() => setShowProfanityModal(false)} 
      />
    </section>
  );
}
